<template>
  <RCard class="notes">
    <FormCreateNote
      :showing.sync="showForm"
      :namespace="serviceNamespace"
      :editable="editable"
      @refresh="getNotes"
      @close="closeForm"
    />

    <Grid gtr="auto 1fr" class="full-height" align-items="start">
      <Grid gaf="column" justify-content="space-between" align-items="center">
        <slot name="title">
          <div class="text-subtitle-1 text-secondary-2">Notes</div>
        </slot>

        <PlusIcon @click.native="showNoteForm" />
      </Grid>

      <Grid class="relative-position q-mt-md" gap="var(--r-space-md)">
        <template v-for="note of notesFlatArray">
          <div v-if="note.isGroup" :key="`group-${note.id}`">
            <span class="text-body-4 text-secondary-1">{{ note.entity | capitalize }} notes </span>
            <router-link :to="{ name: note.entity, params: { id: note.id } }" class="text-body-1 text-link-blue">
              ({{ note.entity_name }})
            </router-link>
          </div>

          <Grid v-else :key="`note-${note.id}`" gtc="auto 1fr auto" class="note q-pr-sm" gap="var(--r-space-sm)">
            <div class="user-initials text-body-3 text-white-1">
              {{ note.initials }}
            </div>

            <Grid class="comment" gap="var(--r-space-sm)">
              <Grid gaf="column" align-items="center" gap="var(--r-space-sm)" justify-content="start">
                <div class="text-body-3 text-secondary-5">{{ note.created_by.name }}</div>
                <div class="text-body-1 text-grey3">{{ formatDateTimeForLocale(note.created_at) }}</div>
              </Grid>

              <div class="text-body-1 text-secondary-5" v-html="note.body" />
            </Grid>

            <DotsIcon
              v-if="note.entity === entity.name || editMode"
              :popup-items="popupItems"
              @popup-item="onPopupItem($event.name, note)"
            />
          </Grid>

          <div v-if="!note.isGroup" :key="`line-${note.id}`" class="line" :class="{ shadow6: note.isLast }"></div>
        </template>

        <QInnerLoading :showing="!notesFlatArray">
          <QSpinnerHourglass size="3.5rem" color="primary-1" />
        </QInnerLoading>

        <Grid v-if="notesFlatArray && !notesFlatArray.length" justify-content="center" justify-items="center">
          <NoData />
          <span>There are no notes created yet</span>
        </Grid>
      </Grid>
    </Grid>
  </RCard>
</template>

<script>
import { DataService } from '@/api'
// Libraries
import { get } from 'lodash'
// Components
import FormCreateNote from '@/components/forms/FormCreateNote'
import PlusIcon from '@/ui/icons/PlusIcon'
import DotsIcon from '@/ui/icons/DotsIcon'
import NoData from '@/reusable/svg-icons/NoData'

export default {
  provide: function() {
    return {
      service: this.getService()
    }
  },

  components: {
    FormCreateNote,
    PlusIcon,
    DotsIcon,
    NoData
  },

  props: {
    entity: {
      type: Object,
      required: true
    },
    storeNamespace: {
      type: String,
      required: true
    },
    grouped: {
      type: Boolean,
      default: true
    },
    editMode: Boolean
  },

  data: () => ({
    showForm: false,
    editable: null,
    notesCollection: null,
    popupItems: [
      { name: 'edit', label: 'Edit' },
      { name: 'delete', label: 'Delete', textColorClass: 'text-danger-1' }
    ]
  }),

  computed: {
    notesFlatArray() {
      if (!this.notesCollection) return null

      if (!this.grouped) {
        return this.notesCollection.map((note, index) => ({
          ...note,
          initials: this.getNameInitials(note.created_by.name),
          isGroup: false,
          isLast: index === this.notesCollection.length - 1
        }))
      }

      return this.notesCollection.reduce((acc, curr) => {
        const notes = curr.notes.map(note => ({
          ...note,
          entity: curr.entity,
          initials: this.getNameInitials(note.created_by.name)
        }))

        notes[notes.length - 1].isLast = true

        if (curr.entity !== this.entity.name) {
          acc.push({ isGroup: true, ...curr })
        }

        acc.push(...notes)

        return acc
      }, [])
    },

    serviceNamespace() {
      return `${this.storeNamespace}/${this.entity.id}/notes`
    }
  },

  created() {
    this.getNotes()
  },

  methods: {
    getService() {
      return new DataService(this.serviceNamespace)
    },

    getNotes() {
      const service = this.getService()
      return service
        .get({
          context: {
            grouped: this.grouped
          }
        })
        .then(response => (this.notesCollection = response.data.result))
        .catch(err => new Error(err))
    },

    showNoteForm() {
      this.showForm = true
    },

    saveEdit(event) {
      let params = {
        value: event,
        id: event.id
      }
      const service = this.getService()
      return service.update(params).then(({ data }) => {
        if (!get(data, 'messages.errors')) {
          this.getNotes()
        }
      })
    },

    deleteNote(note) {
      const service = this.getService()
      return service.delete({ id: note.id }).then(() => {
        this.getNotes()
      })
    },

    closeForm() {
      this.showForm = false
      this.$set(this, 'editable', null)
    },

    editNote(note) {
      this.$set(this, 'editable', note)
      this.showForm = true
    },

    getNameInitials(name) {
      const namesArray = name.trim().split(' ')
      if (namesArray.length === 1) return `${namesArray[0].charAt(0)}`
      else return `${namesArray[0].charAt(0)}${namesArray[namesArray.length - 1].charAt(0)}`
    },

    onPopupItem(eventName, note) {
      if (eventName === 'delete') this.deleteNote(note)
      else if (eventName === 'edit') this.editNote(note)
    }
  }
}
</script>

<style lang="scss" scoped>
.notes {
  min-height: 200px;

  .actions {
    .active {
      border-bottom: 1px solid var(--r-primary-1);
    }
  }

  .note {
    .user-initials {
      border-radius: var(--r-space-md);
      background: var(--r-graph-to-renew);
      width: 28px;
      height: 28px;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }

  .line {
    height: 1px;
    border-bottom: 1px solid var(--r-white-5);
  }
}
</style>
